import SecureStore from './SecureStore';
import config from '../config';
import { pinnedFetch } from './pinnedFetch';
import logger from '../utils/logger';
import { translations } from '../translations';
import {
    LoginCredentials, User, KycSubmission, Company,
    PaginatedResponse, DashboardData, AuditLog, InvitationDetails, AppNotification, KycData
} from '../types';
import { AppError, ErrorCode, parseError } from '../types/errors';

const getAuthHeaders = async (token?: string) => {
    let authToken = token;
    if (!authToken) {
        try {
            // 🔒 SECURITY: Get token from SecureStore only
            // AsyncStorage fallback removed in v2.0.0
            const secureToken = await SecureStore.getItemAsync('authToken');
            if (secureToken) authToken = secureToken;
        } catch (e) {
            logger.error("Could not get auth token from SecureStore", e as Error);
        }
    }

    const headers: HeadersInit = {
        'Content-Type': 'application/json',
        // Identify mobile app requests to backend (for CSRF exemption and analytics)
        'X-Mobile-App': 'true',
        'User-Agent': 'VerifyGate-Mobile/2.0.0',
    };
    if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
    }
    return headers;
};

async function handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    
    if (response.ok) {
        if (contentType && contentType.includes('application/json')) {
            return response.json();
        }
        return response.text() as unknown as T;
    }

    // Handle error responses
    let errorData;
    if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
    } else {
        const textResponse = await response.text();
        errorData = { message: textResponse };
    }
    
    // Error response'u log'la (hem development hem production)
    // Login hataları için özellikle detaylı log
    const isLoginError = response.url?.includes('/auth/login');
    const logLevel = isLoginError ? 'error' : 'warn';
    
    // Error data'yı detaylı log'la (JSON stringify ile tam detay)
    const errorDataString = JSON.stringify(errorData, null, 2);
    
    logger[logLevel]('API error response', {
        status: response.status,
        statusText: response.statusText,
        endpoint: response.url,
        errorData: errorData, // Object olarak da gönder (structured logging için)
        errorDataString: errorDataString, // String olarak da gönder (tam detay için)
        errorCode: errorData.code,
        errorMessage: errorData.message || errorData.error,
        validationErrors: errorData.errors || errorData.validationErrors || errorData.array,
        // Login için özel detaylar
        ...(isLoginError && {
            requiresVerification: errorData.requiresVerification,
            isEmailVerified: errorData.isEmailVerified,
            isActive: errorData.isActive,
            fullErrorData: errorDataString // Login için tam error data
        })
    });

    // Map HTTP status to error code
    let errorCode: ErrorCode = ErrorCode.UNKNOWN_ERROR;
    
    switch (response.status) {
        case 400:
            // 400 hatası için backend'den gelen mesajı kontrol et
            if (errorData.code === 'INVALID_CREDENTIALS' || 
                errorData.message?.toLowerCase().includes('invalid credentials') ||
                errorData.message?.toLowerCase().includes('geçersiz') ||
                errorData.message?.toLowerCase().includes('ungültig')) {
                errorCode = ErrorCode.INVALID_CREDENTIALS;
            } else {
                errorCode = ErrorCode.VALIDATION_ERROR;
            }
            break;
        case 401:
            errorCode = errorData.code === 'INVALID_CREDENTIALS' 
                ? ErrorCode.INVALID_CREDENTIALS 
                : ErrorCode.INVALID_TOKEN;
            break;
        case 403:
            errorCode = ErrorCode.INSUFFICIENT_PERMISSIONS;
            break;
        case 404:
            errorCode = ErrorCode.NOT_FOUND;
            break;
        case 409:
            errorCode = ErrorCode.CONFLICT;
            break;
        case 429:
            errorCode = ErrorCode.LIMIT_EXCEEDED;
            break;
        case 500:
            errorCode = ErrorCode.INTERNAL_SERVER_ERROR;
            break;
        case 503:
            errorCode = ErrorCode.SERVICE_UNAVAILABLE;
            break;
        default:
            if (response.status >= 500) {
                errorCode = ErrorCode.SERVER_ERROR;
            }
    }
    
    // Get default language for error message (fallback to English)
    const defaultLang = 'en' as const;
    
    // Backend'den gelen field-level validation errors'ı kontrol et
    // express-validator errors.array() formatını da destekle
    let fieldErrors: Record<string, string | string[]> = {};
    
    if (errorData.errors) {
        // Eğer errors bir array ise (express-validator formatı)
        if (Array.isArray(errorData.errors)) {
            // express-validator formatı: [{ param: 'email', msg: 'Invalid email' }, ...]
            errorData.errors.forEach((err: { param?: string; msg?: string }) => {
                if (err.param && err.msg) {
                    fieldErrors[err.param] = err.msg;
                }
            });
        } else if (typeof errorData.errors === 'object') {
            // Object formatı: { email: 'Invalid email', password: 'Required' }
            fieldErrors = errorData.errors;
        }
    } else if (errorData.validationErrors) {
        fieldErrors = errorData.validationErrors;
    } else if (errorData.array && Array.isArray(errorData.array)) {
        // express-validator errors.array() direkt formatı
        errorData.array.forEach((err: { param?: string; msg?: string }) => {
            if (err.param && err.msg) {
                fieldErrors[err.param] = err.msg;
            }
        });
    }
    
    // Backend'den gelen mesajı öncelikle kullan, yoksa error code'a göre mesaj göster
    let message = errorData.message || errorData.error || translations[defaultLang].unknownError;
    
    // Eğer field-level errors varsa, ilk hatayı genel mesaj olarak kullan
    if (Object.keys(fieldErrors).length > 0) {
        const firstError = Object.values(fieldErrors)[0];
        if (typeof firstError === 'string') {
            message = firstError;
        } else if (Array.isArray(firstError) && firstError.length > 0) {
            message = firstError[0];
        }
    }
    
    // Eğer mesaj yoksa ve error code'a göre mesaj varsa onu kullan
    if (!message || message === translations[defaultLang].unknownError) {
        const errorMessages: Partial<Record<ErrorCode, string>> = {
            [ErrorCode.INVALID_CREDENTIALS]: 'Invalid email or password',
            [ErrorCode.VALIDATION_ERROR]: 'Please check your input',
            [ErrorCode.INVALID_TOKEN]: 'Invalid or expired token',
            [ErrorCode.INSUFFICIENT_PERMISSIONS]: 'Insufficient permissions',
            [ErrorCode.NOT_FOUND]: 'Resource not found',
            [ErrorCode.CONFLICT]: 'Conflict occurred',
            [ErrorCode.LIMIT_EXCEEDED]: 'Limit exceeded',
            [ErrorCode.INTERNAL_SERVER_ERROR]: 'Internal server error',
            [ErrorCode.SERVICE_UNAVAILABLE]: 'Service temporarily unavailable',
            [ErrorCode.UNKNOWN_ERROR]: 'An unknown error occurred',
        };
        message = errorMessages[errorCode] || message;
    }
    
    throw new AppError(message, errorCode, {
        statusCode: response.status,
        details: {
            ...errorData,
            fieldErrors, // Field-level errors'ı ayrıca sakla
        },
    });
}

// Timeout ve retry configuration for Render sleep mode
const API_TIMEOUT = 60000; // 60 saniye (uyku modu için yeterli)
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 saniye

interface ApiRequestBody {
  [key: string]: unknown;
}

// LoginCredentials tipini ApiRequestBody ile uyumlu hale getir
type RequestBody = ApiRequestBody | LoginCredentials | { email: string; password: string } | { [key: string]: unknown };

interface ApiRequestParams {
  [key: string]: string | number | boolean | null | undefined;
}

const apiRequest = async <T>(
    method: string, 
    endpoint: string, 
    body?: RequestBody | null, 
    params?: ApiRequestParams | null, 
    token?: string,
    retryCount = 0
): Promise<T> => {
    const url = new URL(`${config.API_BASE_URL}${endpoint}`);
    if (params) {
        Object.keys(params).forEach(key => {
            if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
                url.searchParams.append(key, String(params[key]));
            }
        });
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
        const headers = await getAuthHeaders(token);
        const requestConfig: RequestInit = {
            method,
            headers,
            signal: controller.signal,
        };

        if (body) {
            requestConfig.body = JSON.stringify(body);
            
            // Login istekleri için detaylı log (production'da da)
            if (endpoint === '/auth/login') {
                logger.info('Login request', {
                    endpoint,
                    url: url.toString(),
                    method,
                    hasBody: !!body,
                    bodyKeys: Object.keys(body),
                    headers: {
                        'Content-Type': headers['Content-Type'],
                        'X-Mobile-App': headers['X-Mobile-App'],
                        'User-Agent': headers['User-Agent'],
                        hasAuth: !!headers['Authorization']
                    }
                });
            }
        }

        const response = await pinnedFetch(url.toString(), requestConfig);
        clearTimeout(timeoutId);
        
        // Login response'ları için detaylı log
        if (endpoint === '/auth/login') {
            logger.info('Login response', {
                endpoint,
                status: response.status,
                statusText: response.statusText,
                ok: response.ok,
                headers: {
                    'content-type': response.headers.get('content-type')
                }
            });
        }
        
        return handleResponse<T>(response);
    } catch (error: unknown) {
        clearTimeout(timeoutId);
        
        // Parse error to AppError
        const appError = parseError(error);
        
        // Retry for network errors (Render sleep mode support)
        if (appError.retryable && retryCount < MAX_RETRIES) {
            logger.warn(`API request failed, retrying... (${retryCount + 1}/${MAX_RETRIES})`, {
                endpoint,
                errorCode: appError.code,
                errorMessage: appError.message
            });
            
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
            return apiRequest<T>(method, endpoint, body, params, token, retryCount + 1);
        }
        
        // Log error with more details
        logger.error('API request failed', {
            endpoint,
            method,
            errorCode: appError.code,
            errorMessage: appError.message,
            statusCode: appError.statusCode,
            retryCount,
            originalError: error instanceof Error ? {
                name: error.name,
                message: error.message,
                stack: error.stack
            } : error
        });
        
        throw appError;
    }
};

// Health check endpoint (Render uyku modu kontrolü için)
export const checkApiHealth = (): Promise<{ status: string; timestamp: string; services?: Record<string, unknown> }> =>
    apiRequest('GET', '/health');

// Auth
export const login = (credentials: LoginCredentials): Promise<{ user: User, token: string, refreshToken: string, twoFactorRequired?: boolean, tempToken?: string }> =>
    apiRequest('POST', '/auth/login', credentials);

export const register = (data: { email: string; password: string; name: string; phone?: string }): Promise<User> =>
    apiRequest('POST', '/auth/register', data);

export const verifyCode = (data: { email: string, code: string }): Promise<{ user: User, token: string, refreshToken: string }> =>
    apiRequest('POST', '/auth/verify', data);

export const forgotPassword = (email: string): Promise<{ message: string }> =>
    apiRequest('POST', '/auth/forgot-password', { email });

export const resetPassword = (data: { token: string, password: string }): Promise<{ message: string }> =>
    apiRequest('POST', '/auth/reset-password', data);
    
export const getInvitationDetails = (token: string): Promise<InvitationDetails> =>
    apiRequest('GET', `/auth/invitation/${token}`);

export const verifyCompanyInvite = (invitationToken: string): Promise<{ success: boolean; company?: { id?: string; name: string; email?: string; code?: string } }> =>
    apiRequest('POST', '/companies/verify-invite', { invitationToken });

export const completeInvitation = (data: { token: string, password: string }): Promise<{ user: User, token: string, refreshToken: string }> =>
    apiRequest('POST', '/auth/invitation/complete', data);

// 2FA
export const generate2FA = (token: string): Promise<{ secret: string, qrCode: string }> =>
    apiRequest('POST', '/2fa/generate', null, null, token);

export const verify2FA = (data: { code: string }, token: string): Promise<{ message: string }> =>
    apiRequest('POST', '/2fa/verify', data, null, token);

export const disable2FA = (data: { code: string }, token: string): Promise<{ message: string }> =>
    apiRequest('POST', '/2fa/disable', data, null, token);

export const verify2faLogin = (data: { code: string, tempToken: string }): Promise<{ user: User, token: string, refreshToken: string }> =>
    apiRequest('POST', '/2fa/login/verify', data);

// Submissions
export const createSubmission = (data: KycData, token: string): Promise<KycSubmission> =>
    apiRequest('POST', '/submissions', data, null, token);

export const getMySubmissions = (token: string): Promise<KycSubmission[]> =>
    apiRequest('GET', '/submissions/me', null, null, token);
    
export const getAdminSubmissions = (params: { page?: number; limit?: number; status?: string }, token: string): Promise<PaginatedResponse<KycSubmission>> =>
    apiRequest('GET', '/submissions', null, params, token);

export const updateSubmissionStatus = (id: string, status: string, rejectionReason: string | undefined, token: string): Promise<KycSubmission> =>
    apiRequest('PUT', `/submissions/${id}/status`, { status, rejectionReason }, null, token);

export const extractOcrData = (base64Image: string, token: string): Promise<{ text?: string; fields?: Record<string, unknown>; confidence?: number }> =>
    apiRequest('POST', '/submissions/ocr', { image: base64Image }, null, token);

// Companies
export const getAllCompanies = (params: { page?: number; limit?: number; sortBy?: string; sortOrder?: string; search?: string }, token: string): Promise<PaginatedResponse<Company>> =>
    apiRequest('GET', '/companies', null, params, token);

export const getCompanyById = (id: string, token: string): Promise<Company> =>
    apiRequest('GET', `/companies/${id}`, null, null, token);

export const updateCompany = (id: string, data: Partial<Company>, token: string): Promise<Company> =>
    apiRequest('PUT', `/companies/${id}`, data, null, token);

export const deleteCompany = (id: string, token: string): Promise<{ message: string }> =>
    apiRequest('DELETE', `/companies/${id}`, null, null, token);

// Users (Company Admin & Self)
export const getAllUsers = (params: { page?: number; limit?: number; role?: string; sortBy?: string; sortOrder?: string; search?: string }, token: string): Promise<PaginatedResponse<User>> =>
    apiRequest('GET', '/users', null, params, token);

export const getCompanyUsers = (companyId: string, params: { page?: number; limit?: number }, token: string): Promise<PaginatedResponse<User>> =>
    apiRequest('GET', `/companies/${companyId}/users`, null, params, token);

export const inviteUser = (data: { email: string, role: string, companyId: string }, token: string): Promise<{ message: string }> =>
    apiRequest('POST', '/users/invite', data, null, token);

export const updateUser = (id: string, data: Partial<User>, token: string): Promise<User> =>
    apiRequest('PUT', `/users/${id}`, data, null, token);

export const deleteUser = (id: string, token: string): Promise<{ message: string }> =>
    apiRequest('DELETE', `/users/${id}`, null, null, token);
    
export const adminResetPassword = (userId: string, token: string): Promise<{ message: string }> =>
    apiRequest('POST', `/users/${userId}/reset-password`, null, null, token);
    
export const registerPushToken = (pushToken: string, token: string): Promise<{ message: string }> =>
    apiRequest('PUT', '/users/me/push-token', { token: pushToken }, null, token);
    
// Dashboard
export const getDashboardStats = (token: string): Promise<DashboardData> =>
    apiRequest('GET', '/dashboard/stats', null, null, token);

// Audit Logs
export const getAuditLogs = (params: { page?: number; limit?: number; userId?: string; action?: string }, token: string): Promise<PaginatedResponse<AuditLog>> =>
    apiRequest('GET', '/audit', null, params, token);

// Notifications
export const getNotifications = (token: string, params?: { page?: number; limit?: number; unread?: boolean }): Promise<PaginatedResponse<AppNotification>> =>
    apiRequest('GET', '/notifications', null, params, token);

export const markNotificationAsRead = (token: string, id: string): Promise<AppNotification> =>
    apiRequest('PUT', `/notifications/${id}/read`, null, null, token);

export const markAllNotificationsAsRead = (token: string): Promise<{ message: string }> =>
    apiRequest('PUT', '/notifications/read-all', null, null, token);

export const deleteNotification = (token: string, id: string): Promise<{ message: string }> =>
    apiRequest('DELETE', `/notifications/${id}`, null, null, token);

// Liveness
export const verifyLivenessFinal = (
  images: string[],
  challengeTypes: string[],
  confidences: number[],
  language: string,
  token: string,
  antiSpoofingDetails?: Record<string, unknown>
): Promise<{ verified: boolean; overallScore?: number; challengeResults?: { type: string; passed: boolean; score: number }[]; reason?: string }> => {
  return apiRequest('POST', '/kyc/verify-liveness', {
    images,
    challengeTypes,
    confidences,
    language,
    antiSpoofingDetails
  }, undefined, token);
};

// Legacy function (for backward compatibility)
export const verifyLivenessFinalLegacy = (
    images: string[], 
    challenges: string[], 
    localConfidences: number[], 
    language: string,
    token: string
): Promise<{ 
    verified: boolean, 
    overallScore: number, 
    samePerson: boolean,
    spoofingDetected: boolean,
    challengeResults: {challenge: string; passed: boolean; confidence: number}[],
    reason?: string 
}> =>
    apiRequest('POST', '/ocr/verify-liveness-final', { 
        images, 
        challenges, 
        localConfidences, 
        language 
    }, null, token);